/**
 * routes.ts
 *
 * Single source of truth for in-app route paths AND a typed builder set.
 *
 * Why this exists: we kept typing `/hr-v1/${id}` in many components and
 * had at least one bug where the literal `/hr-v1/:cycleId` placeholder
 * leaked into the UI (route copy/paste lost the interpolation). Building
 * routes here means:
 *   - One place to update if a path changes
 *   - TypeScript catches missing arguments at compile time
 *   - You cannot accidentally navigate to the bare placeholder
 *
 * Conventions:
 *   - `PATH_*` constants are the React-Router pattern strings (with :id)
 *     used in <Route path={...}>.
 *   - `routes.*` functions build the resolved URL with real ids.
 *
 * If you add a new route to App.tsx, add it here too so call sites can
 * use the builder. Don't hand-template URLs at the call site.
 */

// ─── Pattern strings (consumed by <Route path={…}>) ─────────────────

export const PATH_CYCLES_LIST          = '/cycles';
export const PATH_HR_V1_CYCLE          = '/hr-v1/:cycleId';
export const PATH_HR_V1_LAUNCH_EPR     = '/hr-v1/:cycleId/launch/epr';
export const PATH_HR_V1_LAUNCH_360     = '/hr-v1/:cycleId/launch/360';
export const PATH_HR_V1_REVIEWS        = '/hr-v1/reviews';
export const PATH_HR_V1_CALIBRATION    = '/hr-v1/calibration';
export const PATH_HR_V1_APPRAISALS     = '/hr-v1/appraisals';
export const PATH_HR_V1_APPEALS        = '/hr-v1/appeals';
export const PATH_MAGIC_LINK_DISPATCH  = '/m/:token';

// ─── Builders ───────────────────────────────────────────────────────

/**
 * Build a URL to a specific hr_v1 cycle's workflow console.
 * Throws (not silently returns the placeholder) when the id is falsy —
 * catches the common bug of passing `cycle?.id` from a nullable cycle.
 */
function hrV1Cycle(cycleId: string, opts?: { stepHash?: number; stepQuery?: number }): string {
  if (!cycleId || cycleId === ':cycleId') {
    throw new Error('routes.hrV1Cycle: cycleId is required and must not be the placeholder');
  }
  const base = `/hr-v1/${cycleId}`;
  if (opts?.stepQuery) return `${base}?step=${opts.stepQuery}`;
  if (opts?.stepHash)  return `${base}#step-card-${opts.stepHash}`;
  return base;
}

function hrV1LaunchEpr(cycleId: string): string {
  if (!cycleId || cycleId === ':cycleId') {
    throw new Error('routes.hrV1LaunchEpr: cycleId is required and must not be the placeholder');
  }
  return `/hr-v1/${cycleId}/launch/epr`;
}

function hrV1Launch360(cycleId: string): string {
  if (!cycleId || cycleId === ':cycleId') {
    throw new Error('routes.hrV1Launch360: cycleId is required and must not be the placeholder');
  }
  return `/hr-v1/${cycleId}/launch/360`;
}

function cyclesList(): string {
  return PATH_CYCLES_LIST;
}

function hrV1Reviews(): string {
  return PATH_HR_V1_REVIEWS;
}

function hrV1Calibration(): string {
  return PATH_HR_V1_CALIBRATION;
}

function hrV1Appraisals(): string {
  return PATH_HR_V1_APPRAISALS;
}

function hrV1Appeals(): string {
  return PATH_HR_V1_APPEALS;
}

function magicLink(token: string): string {
  if (!token) {
    throw new Error('routes.magicLink: token is required');
  }
  return `/m/${token}`;
}

/**
 * Canonical base URL for all PUBLIC token URLs (`/m/<token>`,
 * `/r/<token>`, `/360/<token>`). Single source of truth so HR opening
 * an admin page on the bare `*.web.app` URL doesn't generate tokens
 * pointing to that hostname instead of the customer-facing one.
 *
 * Priority:
 *   1. import.meta.env.VITE_EPR_URL  — set per deploy via .env files
 *   2. window.location.origin        — runtime fallback in browser
 *   3. http://localhost:3003         — Vite dev default
 *
 * ENGINEERING.md §3e — every public-token URL composition MUST go
 * through here. Hand-templated `${window.location.origin}/<route>/<token>`
 * is a refused pattern (it leaks the hostname when HR opens admin
 * pages on the bare *.web.app URL).
 */
export function getEprBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_EPR_URL as string | undefined;
  const fromBrowser = typeof window !== 'undefined' ? window.location.origin : undefined;
  return (fromEnv?.trim() || fromBrowser || 'http://localhost:3003').replace(/\/+$/, '');
}

/**
 * Absolute magic-link URL — e.g.
 *   staging:    https://epr-staging.rascalrepublic.com/m/abc
 *   production: https://epr.rascalrepublic.com/m/abc
 * Base resolved at runtime via getEprBaseUrl() — see VITE_EPR_URL per
 * .env.<mode>.
 */
function publicMagicLinkUrl(token: string): string {
  return `${getEprBaseUrl()}${magicLink(token)}`;
}

/** Absolute OPTICS legacy invitation URL. */
function publicReviewInviteUrl(token: string): string {
  if (!token) throw new Error('routes.publicReviewInviteUrl: token required');
  return `${getEprBaseUrl()}/r/${token}`;
}

/** Absolute OPTICS legacy 360 invitation URL. */
function public360InviteUrl(token: string): string {
  if (!token) throw new Error('routes.public360InviteUrl: token required');
  return `${getEprBaseUrl()}/360/${token}`;
}

export const routes = {
  cyclesList,
  hrV1Cycle,
  hrV1LaunchEpr,
  hrV1Launch360,
  hrV1Reviews,
  hrV1Calibration,
  hrV1Appraisals,
  hrV1Appeals,
  magicLink,
  // Absolute public URLs — for tokens distributed outside the app
  // (email, WhatsApp, copy-paste). Always pinned to the canonical
  // domain via getEprBaseUrl().
  publicMagicLinkUrl,
  publicReviewInviteUrl,
  public360InviteUrl,
};
